import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  ConnectionsService,
  PaginatedConnectedFacilityOptimizedList,
  SourceTypeEnum,
  AssignmentsService,
  PaginatedAssignmentListList,
  AssignmentDetail,
  PaginatedTimeCardListList,
  TimeCardList,
} from '../../../../../js/api';
import type { ConnectedFacility } from '../../../../../js/api';

import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useProvider } from '../../../../../js/app/hooks/providers';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { useUserProgress } from '../../../../../js/app/hooks/user-progress';
// import { ProviderSchedule } from '../../../../../js/pages/ProviderDashboard/ProviderSchedule';
import { Spinner } from '../../../components/spinner';
import { StatusCodes } from '../../../constants/workflows';
import { isAwaitingProviderResponse } from '../../../utils/connectionStatus';
import { ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY } from '../Assignments/queryKeys';
import { PROVIDER_CONNECTIONS_LIST_QUERY_KEY } from '../Connections/queryKeys';
import { PROVIDER_TIMECARDS_LIST_QUERY_KEY } from '../Timecards/queryKeys';
import { AllSetStatus } from './AllSetStatus';
import { AssignmentsNew } from './AssignmentsNew';
import { AssignmentsUpdated } from './AssignmentsUpdated';
// import { AvailabilityUrlCopy } from './AvailabilityUrlCopy';
import { ConnectionsPendingProviderResponse } from './ConnectionsPendingProviderResponse';
import ConnectionsProviderActionRequested from './ConnectionsProviderActionRequested';
import { HomeSection } from './HomeSection';
import { ProfileCompletionTasks } from './ProfileCompletionTasks';
import { ProfileProgress } from './ProfileProgress';
import { ProviderScheduleCalendar } from './ProviderScheduleCalendar';
import TimecardReview from './TimecardReview';

export function ProviderHomeWrapper() {
  const { user } = useCurrentUser();
  const { provider, isProviderLoading } = useProvider();
  const providerId = user?.provider_id;
  const { progress, isProfileComplete } = useUserProgress(
    user?.provider_completed_registration_steps
  );

  useDocumentTitle('Home');

  const isProviderSourced: boolean = provider?.source_type === SourceTypeEnum.ORGANIZATION;

  const connectionsQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    // Code filter (server-side), so REOPENED must be named; the client-side split below
    // uses the category kind instead.
    status: [StatusCodes.REQUESTED, StatusCodes.REOPENED, StatusCodes.PROV_ACTION_REQ],
  };

  const assignmentsQueryParams: Parameters<typeof AssignmentsService.assignmentsAssignmentList>[0] =
    {
      status: [StatusCodes.NEW, StatusCodes.PROV_REV],
      limit: 100,
    };

  const timecardQueryParams: Parameters<typeof AssignmentsService.assignmentsTimecardList>[0] = {
    status: [StatusCodes.NEW, StatusCodes.INFO_NEEDED],
    limit: 100,
  };

  const {
    data: connectionRequests = [],
    isLoading: isConnectionRequestsLoading,
    error: connectionRequestsError,
  } = useQuery({
    queryKey: [PROVIDER_CONNECTIONS_LIST_QUERY_KEY, connectionsQueryParams],
    queryFn: () => ConnectionsService.connectionsConnectedFacilityList(connectionsQueryParams),
    select: (data: PaginatedConnectedFacilityOptimizedList) => data.results as ConnectedFacility[],
    staleTime: 30_000,
  });

  const {
    data: assignments = [],
    isLoading: isAssignmentsLoading,
    error: assignmentsError,
  } = useQuery({
    queryKey: [ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY, 'needs-attention', assignmentsQueryParams],
    queryFn: () => AssignmentsService.assignmentsAssignmentList(assignmentsQueryParams),
    select: (data: PaginatedAssignmentListList) => data.results as AssignmentDetail[],
    staleTime: 30_000,
  });

  const {
    data: timecards = [],
    isLoading: isTimecardsLoading,
    error: timecardsError,
  } = useQuery({
    queryKey: [PROVIDER_TIMECARDS_LIST_QUERY_KEY, 'needs-attention', timecardQueryParams],
    queryFn: () => AssignmentsService.assignmentsTimecardList(timecardQueryParams),
    select: (data: PaginatedTimeCardListList) => data.results as TimeCardList[],
    staleTime: 30_000,
  });

  const newAssignmentRequests = useMemo(
    () => assignments.filter((a) => a.current_status.code === StatusCodes.NEW),
    [assignments]
  );

  const updatedAssignmentRequests = useMemo(
    () => assignments.filter((a) => a.current_status.code === StatusCodes.PROV_REV),
    [assignments]
  );

  const hasPendingProfileTasks = useMemo(() => {
    if (!provider) return false;

    const serviceAgreementComplete = !provider.needs_contract_signed;

    if (isProviderSourced) {
      return !serviceAgreementComplete;
    }

    // the legacy cv URL or a CV in the documents framework (the API decides)
    const hasCv = Boolean(provider.has_cv ?? provider.cv);
    const references = Array.isArray(provider.references) ? provider.references : [];
    const professionalTraitsComplete = hasCv && references.length > 0;

    const licenses = Array.isArray(provider.licenses) ? provider.licenses : [];

    const isMedicalLicenseWithFile = (obj: unknown): boolean => {
      if (typeof obj !== 'object' || obj === null) return false;
      const o = obj as { [key: string]: unknown };
      const isMedical = typeof o['type'] === 'string' && o['type'] === 'MEDICAL';
      const hasFile = 'file' in o && Boolean(o['file']);
      return isMedical && hasFile;
    };

    const licensesComplete = licenses.some(isMedicalLicenseWithFile);
    const questionnaireComplete = Boolean(provider.completed_questionnaire);

    return (
      !professionalTraitsComplete ||
      !licensesComplete ||
      !questionnaireComplete ||
      !serviceAgreementComplete
    );
  }, [provider]);

  const connectionsPendingProviderResponse = useMemo(
    () => connectionRequests.filter((c) => isAwaitingProviderResponse(c.current_status)),
    [connectionRequests]
  );

  const connectionsProvActionReq = useMemo(
    () => connectionRequests.filter((c) => c.current_status.code === StatusCodes.PROV_ACTION_REQ),
    [connectionRequests]
  );

  // Combined loading state - show loading spinner until all API requests complete
  const isLoading =
    isProviderLoading || isConnectionRequestsLoading || isAssignmentsLoading || isTimecardsLoading;

  // Show the loading spinner while any API request is loading
  if (isLoading) {
    return (
      <div className="tw:flex tw:min-h-[60vh] tw:items-center tw:justify-center">
        <Spinner size="xl" label="Hold please..." />
      </div>
    );
  }

  return (
    <div className="tw:p-4 tw:xl:px-0 tw:max-w-3xl tw:mx-auto tw:w-full">
      {/* Requires Attention Section */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        !timecardsError &&
        (connectionsPendingProviderResponse.length > 0 ||
          newAssignmentRequests.length > 0 ||
          hasPendingProfileTasks ||
          !isProfileComplete) && (
          <HomeSection
            headingText=""
            sectionClassName="tw:mt-0"
            headingClassName="tw:text-warning-section-text"
            components={[
              // Show ProfileProgress if onboarding is not complete
              !isProfileComplete && !isProviderSourced && (
                <div key="profileProgress" className="">
                  <ProfileProgress progress={progress} />
                </div>
              ),
              // Show other components only if onboarding is complete
              <ConnectionsProviderActionRequested
                key="connectionsProvActionReq"
                connectionsProvActionReq={connectionsProvActionReq}
              />,
              <ConnectionsPendingProviderResponse
                key="connectionsPendingProviderResponse"
                connectionsPendingProviderResponse={connectionsPendingProviderResponse}
              />,
              <AssignmentsNew
                key="assignmentsPendingProviderResponse"
                assignmentsPendingProviderResponse={newAssignmentRequests}
              />,
              <AssignmentsUpdated
                key="assignmentsUpdated"
                assignmentsUpdated={updatedAssignmentRequests}
              />,
              <TimecardReview key="timecardReview" timecards={timecards} />,
              <ProfileCompletionTasks key="profileCompletionTasks" />,
            ].filter(Boolean)}
            actionText=""
            actionFunction={undefined}
          />
        )}

      {/* All Set Status - shown when no actions require attention */}
      {!isConnectionRequestsLoading &&
        !connectionRequestsError &&
        !assignmentsError &&
        connectionsPendingProviderResponse.length === 0 &&
        newAssignmentRequests.length === 0 &&
        !hasPendingProfileTasks && (
          <div className="tw:my-2">
            <AllSetStatus
              providerDisplayName={[
                provider?.prefix_name,
                provider?.preferred_name,
                provider?.last_name,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </div>
        )}

      {/* Availability section — provider's schedule calendar (v2). */}
      {providerId && provider?.availability_url && (
        <ProviderScheduleCalendar availabilityUrl={provider.availability_url} />
      )}
    </div>
  );
}
